import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { colors, typo } from '../../theme/tokens';

interface MetricCardProps {
  label: string;
  value: string | number | null | undefined;
  icon?: React.ReactNode;
  bg?: string;
  color?: string;
  tooltip?: string;
  loading?: boolean;
  onClick?: () => void;
}

export default function MetricCard({
  label,
  value,
  icon,
  bg,
  color,
  tooltip,
  loading,
  onClick,
}: MetricCardProps) {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          border: '1px solid rgba(0,0,0,0.06)',
          borderLeft: `4px solid ${colors.bgMuted}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        }}
      >
        <Skeleton width={80} height={14} sx={{ mb: 1 }} />
        <Skeleton width={60} height={32} />
      </Paper>
    );
  }

  const displayValue =
    value === null || value === undefined
      ? 'N/A'
      : typeof value === 'number'
        ? value.toLocaleString()
        : value;

  const accentColor = color || colors.brand;

  const card = (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${accentColor}`,
        bgcolor: bg || colors.bgWhite,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          borderColor: 'rgba(250, 179, 6, 0.4)',
          borderLeftColor: accentColor,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: bg || colors.bgMuted,
              color: color || colors.textPrimary,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...typo.metricLabel, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: 11 }}>{label}</Typography>
          <Typography
            sx={{
              ...typo.metricValue,
              mt: 0.5,
              color: color || colors.textPrimary,
            }}
          >
            {displayValue}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}
